import { DropdownContent, DropdownWrapper } from '@anm/components/Dropdown';
import { flexCenter, flexColumn, flexRow, media } from '@anm/styles/helpers';
import styled, { css } from 'styled-components';

import { getHoverColor } from './helpers';
import { DropdownContentProps, DropdownLabel, ItemContentProps } from './types';

const getDropdownContentStyles = (name: DropdownLabel) =>
  ({
    holidays: css`
      min-width: 577px;
    `,
    industries: css`
      min-width: 737px;
    `,
    'use cases': css`
      min-width: 798px;
    `,
    'social media': css`
      min-width: 436px;
    `,
    'ads & promo': css`
      min-width: 293px;
    `,
    trending: css`
      min-width: 323px;
    `,
    'live streaming': css`
      min-width: 680px;
    `,
    'image templates': css`
      min-width: 230px;
    `
  }[name]);

const getDropdownPositionStyles = (name: DropdownLabel) =>
  ({
    holidays: css`
      transform: translateX(10px);
    `,
    industries: css`
      transform: translateX(37%);
    `,
    'use cases': css`
      transform: translateX(14%);
    `,
    'social media': css`
      transform: translateX(30%);
    `,
    'ads & promo': css`
      transform: translateX(18%);
    `,
    'live streaming': css`
      transform: translateX(50%);
    `,
    'image templates': css`
      left: 0;
      right: unset;
      transform: translateX(0);
    `,
    trending: css`
      transform: translateX(24%);
    `
  }[name]);

const getDropdownResponsivePositionStyles = (name: DropdownLabel) =>
  ({
    holidays: css`
      @media (max-width: 1680px) {
        transform: translateX(35%);
      }
      @media (max-width: 1237px) {
        transform: translateX(5%);
      }
      @media (max-width: 1047px) {
        transform: translateX(35%);
      }
    `,
    industries: css`
      @media (max-width: 1680px) {
        transform: translateX(0);
      }
      @media (max-width: 1406px) {
        transform: translateX(45%);
      }
      @media (max-width: 1047px) {
        transform: translateX(25%);
      }
    `,
    'use cases': css`
      @media (max-width: 1680px) {
        transform: translateX(0);
      }
      @media (max-width: 1539px) {
        transform: translateX(50%);
      }
      @media (max-width: 1237px) {
        transform: translateX(25%);
      }
      @media (max-width: 1047px) {
        transform: translateX(6%);
      }
    `,
    'social media': css`
      @media (max-width: 1237px) {
        transform: translateX(5%);
      }
      @media (max-width: 1000px) {
        transform: translateX(52%);
      }
    `,
    'ads & promo': css`
      @media (max-width: 1303px) and (min-width: 1190px) {
        transform: translateX(0%);
      }
    `,
    'live streaming': css`
      @media (max-width: 1240px) {
        transform: translateX(40%);
      }
    `,
    'image templates': css``,
    trending: css``
  }[name]);

const getInternalEmbedResponsivePositionStyles = (label: string) =>
  ({
    holidays: css``,
    industries: css`
      @media (max-width: 1466px) and (min-width: 1179px) {
        transform: translateX(0%);
      }
      @media (max-width: 1118px) {
        transform: translateX(60%);
      }
    `,
    'use cases': css`
      @media (max-width: 1466px) {
        transform: translateX(0%);
      }
      @media (max-width: 1343px) {
        transform: translateX(34%);
      }
    `,
    'social media': css`
      @media (max-width: 1009px) and (min-width: 820px) {
        transform: translateX(0%);
      }
      @media (max-width: 819px) {
        transform: translateX(58%);
      }
    `,
    'ads & promo': css`
      @media (max-width: 1177px) and (min-width: 1005px) {
        transform: translateX(0%);
      }
    `,
    'live streaming': css``,
    'image templates': css``,
    trending: css``
  }[label]);

export const DropdownContentWrapper = styled.div<DropdownContentProps>`
  ${flexRow};
  padding: 20px 20px 10px;
  ${({ label }) => label && getDropdownContentStyles(label)}
`;

const DropdownContentPositionStyles = css<ItemContentProps>`
  ${DropdownContent} {
    right: 0;
    left: unset;
    border-radius: 10px;

    ${({ label }) => label && getDropdownPositionStyles(label)};
    ${({ label, isInternalEmbed }) => label && !isInternalEmbed && getDropdownResponsivePositionStyles(label)};
    ${({ label, isInternalEmbed }) => label && isInternalEmbed && getInternalEmbedResponsivePositionStyles(label)};
  }
`;

const dropdownHoverStyles = css<ItemContentProps>`
  &:hover {
    background: ${({ color }) => getHoverColor(color)};
    & > div {
      &:after {
        transform: rotate(${({ isOpened }) => (isOpened ? '180deg' : '0')});
      }
    }
  }
`;

const dropdownStyles = css<ItemContentProps>`
  & > ${DropdownWrapper} {
    min-height: 50px;
    &:after {
      right: 30px;
      position: absolute;
      filter: brightness(24%) contrast(90%);
    }
    &:before {
      display: none;
    }
    & > div {
      top: 60px;
      &:after,
      &:before {
        width: 100%;
        height: 13px;
        border: none;
        background: transparent;
      }
    }
  }
`;

export const CategoryText = styled.span<{ isDropdown?: boolean }>`
  display: flex;
  color: #292a2b;
  padding: 0 30px;
  padding-right: ${({ isDropdown }) => (isDropdown ? 50 : 30)}px;
`;

export const ItemWrapper = styled.div<ItemContentProps>`
  ${flexCenter};
  margin: 10px 5px 0;
  color: #292a2b;
  cursor: pointer;
  min-height: 50px;
  font-weight: bold;
  border-radius: 5px;
  text-align: center;
  position: relative;
  background: ${({ color }) => color};

  ${dropdownStyles};
  ${dropdownHoverStyles};
  ${DropdownContentPositionStyles};
`;

export const ContentColumn = styled.div`
  ${flexColumn};
`;

export const DropdownItemWrapper = styled.div`
  ${flexRow};
  color: #292a2b;
  padding: 0 30px;
  cursor: pointer;
  min-height: 50px;
  border-radius: 5px;
  align-items: center;
  margin-bottom: 10px;
  text-align: left;
  transition: background 300ms ease-in-out;
  &:hover {
    background: #ebeef5;
  }
`;

const highlightedItemStyles = css`
  order: 2;
  align-self: flex-start;
  margin-left: 30px;
  ${DropdownItemWrapper} {
    width: fit-content;
    background-color: #f5f7fa;
    border: 1px solid rgba(1, 83, 130, 0.2);
    &:hover {
      background: #ebeef5;
    }
  }
`;

export const DropdownItemContent = styled.div<{ isHighlighted?: boolean }>`
  ${({ isHighlighted }) => isHighlighted && highlightedItemStyles}
`;

const listWrapperOnMobile = media.tablet`
   & > a {
    flex: 1 1 0;
   }
`;

const ListWrapper = styled.div`
  ${flexRow};
  ${flexCenter};
  flex-wrap: wrap;
  position: relative;
  margin-bottom: 40px;
  ${listWrapperOnMobile}
`;

export default ListWrapper;
